// EO-1184 — promo expiry: deep link into a promo, and the Heap events that
// cannot be autocaptured.
//
// Layered on EO-1227, which owns the Action Center expiry card, its review
// modal and the promotions-table pills. The card-side analytics are EO-1227's
// too and already work without anything here: it calls
// useHeapCardViewed("promo_expiry", …) for the impression and carries
// data-heap-id="promo-expiry-dismiss" / "promo-expiry-review-extend" on its
// buttons, which Heap autocaptures. Defining rival ids here would only give the
// dashboards two names for one click, so this file deliberately has none.

// Deep-link params: `promoId` opens that promo's drawer on the promotions page,
// `propertyId` says which hotel it belongs to, `src` records which surface sent
// the hotel there.
//
// `propertyId` matters most for the email link, which is opened days later in
// whatever session the recipient happens to have. A promo is fetched as
// (promoId, propertyId), so landing on the wrong hotel does not just show the
// wrong page — it fetches a promo that does not exist for that property and
// opens an empty drawer.
export const PROMO_QUERY_PARAM = Object.freeze({
  promoId: "promoId",
  propertyId: "propertyId",
  source: "src",
});

// Surfaces that deep-link a promo. Values are part of the URL contract: they
// are baked into links already sent out, so changing one silently breaks
// attribution for every link in the wild.
//
// `email` is the live one. EO-1227's card reaches a promo in-app instead — its
// CTA opens the review modal, which hands off through location.state and never
// builds a URL — so CARD is here for a link built outside the app (or if that
// hand-off ever moves to the URL), not for today's card.
export const PROMO_EXPIRY_SOURCE = Object.freeze({
  CARD: "expiry_card",
  EMAIL: "email",
});

export const PROMO_EXPIRY_SOURCES = Object.freeze(
  Object.values(PROMO_EXPIRY_SOURCE)
);

// Path the card and email CTAs point at.
export const PROMOTIONS_PATH = "/promotions";

/**
 * Link the Action Center card and the expiry email should use.
 *
 *   buildPromoDeepLink("123", PROMO_EXPIRY_SOURCE.CARD)
 *     → "/promotions?promoId=123&src=expiry_card"
 *   buildPromoDeepLink("123", PROMO_EXPIRY_SOURCE.EMAIL, 456)
 *     → "/promotions?promoId=123&propertyId=456&src=email"
 *
 * Always pass propertyId for the email link. In-app the hotel is already on the
 * right property, but an email is opened later in an unrelated session.
 *
 * @param {string|number} promoId
 * @param {string} [source] one of PROMO_EXPIRY_SOURCE
 * @param {string|number} [propertyId] hotel the promo belongs to
 * @returns {string}
 */
export const buildPromoDeepLink = (promoId, source, propertyId) => {
  const params = new URLSearchParams({
    [PROMO_QUERY_PARAM.promoId]: String(promoId),
  });
  if (propertyId != null && propertyId !== "") {
    params.set(PROMO_QUERY_PARAM.propertyId, String(propertyId));
  }
  if (source) params.set(PROMO_QUERY_PARAM.source, source);
  return `${PROMOTIONS_PATH}?${params.toString()}`;
};

/**
 * The EO-1184 analytics events, named exactly as the spec table names them so
 * the dashboards can be built against these strings directly.
 *
 *  • cardViewed — sent from the card's impression observer, alongside the shared
 *    "Recommendation Card Viewed" the Action Center's card-position analytics
 *    use. One observer emits both, so they cannot disagree.
 *  • cardDismissed — sent from the card's dismiss handler. The button also keeps
 *    its data-heap-id, which autocaptures under its own name; that is a
 *    different event, not a duplicate of this one.
 *  • ctaClicked — reported where the deep link *lands*, not where it is clicked,
 *    so it covers the expiry email, whose click happens in a mail client and
 *    never reaches us.
 *  • promoExtended — the save button looks the same whether the end date moved
 *    out, moved in, or never changed. Only the app knows which.
 *
 * promo_reactivated is deliberately absent: the spec records it as captured
 * through Heap dashboards with no extra event needed.
 *
 * Attribution rides along on all of them: useHeapIdentify registers actorType,
 * userRole, propertyId and propertyName as Heap event properties, which attach
 * to every event including autocaptured ones.
 */
export const PROMO_EXPIRY_HEAP_EVENT = Object.freeze({
  cardViewed: "expiring_promo_card_viewed",
  cardDismissed: "expiring_promo_card_dismissed",
  ctaClicked: "expiring_promo_cta_clicked",
  promoExtended: "promo_extended",
});
